/**
 * Antila30_1.ts — anchor points for the Antila 30.1 sailboat preset.
 *
 * Body-frame convention used here:
 *   +X = forward (bow)
 *   +Y = up
 *   +Z = starboard (right)
 *   Y = 0 → designed waterline.
 *
 * Source dimensions (manufacturer data):
 *   - hull length 8.85 m, beam 3.20 m, draft 0.44 m (centerboard up,
 *     1.79 m with board down),
 *   - max cabin height 1.91 m, mast 12.2 m above deck.
 *
 * Anchor positions are PLACEHOLDERS — geometrically plausible for an
 * 8.85 m centerboard cruiser. Will be re-derived from named Empties in
 * `antila_30_1.glb` (Buoyancy.*, Lateral.*, Cleat.*, …) once those are
 * authored in Blender and the runtime parser is in place.
 */

import type { BoatAnchorPoints, Vec3 } from "./BoatAnchorPoints";

export function antila30_1Anchors(): BoatAnchorPoints {
  // ---- Mass / buoyancy reference ------------------------------------
  const centerOfMass: Vec3 = [-0.55, -0.18, 0];
  const centerOfBuoyancy: Vec3 = [0, -0.45, 0];

  // ---- Cleats -------------------------------------------------------
  // Bow / stern at ~±48% LWL from CG; midship near max-beam station.
  const cleats = [
    { name: "bow", position: [4.2, 0.85, 0] as Vec3 },
    { name: "stern_port", position: [-4.1, 0.75, -1.5] as Vec3 },
    { name: "stern_starboard", position: [-4.1, 0.75, 1.5] as Vec3 },
    { name: "midship_port", position: [0.5, 0.85, -1.58] as Vec3 },
    { name: "midship_starboard", position: [0.5, 0.85, 1.58] as Vec3 },
    { name: "forepeak", position: [3.4, 0.95, 0] as Vec3 },
  ];

  // ---- Fender hangers ----------------------------------------------
  const fenderXs = [3.1, 1.1, -1.1, -3.1];
  const fenderHangers = fenderXs.flatMap((xFwd) => [
    { side: "port" as const, position: [xFwd, 0.85, -1.65] as Vec3 },
    { side: "starboard" as const, position: [xFwd, 0.85, 1.65] as Vec3 },
  ]);

  // ---- Steering / propulsion ---------------------------------------
  const rudderPivot: Vec3 = [-4.2, -0.28, 0];
  const tillerHandle: Vec3 = [-2.7, 0.35, 0];
  const centerboardPivot: Vec3 = [0.23, -0.1, 0];
  const engineMount: Vec3 = [-4.3, -0.18, 0];
  const propellerPos: Vec3 = [-4.65, -0.5, 0];

  // ---- Rig ---------------------------------------------------------
  const mastBase: Vec3 = [0.88, 0.7, 0];
  const mastTop: Vec3 = [0.88, 12.9, 0];
  const boomAttach: Vec3 = [0.88, 1.8, 0];

  // ---- Buoyancy samples (8 pts) ------------------------------------
  // Will be overridden by Buoyancy.* Empties from the glb once authored.
  const buoyancySamples: Vec3[] = [
    [4.0, -0.55, 0],
    [-4.0, -0.55, 0],
    [2.1, -0.6, -0.95],
    [2.1, -0.6, 0.95],
    [-2.1, -0.6, -0.95],
    [-2.1, -0.6, 0.95],
    [0, -0.3, -1.55],
    [0, -0.3, 1.55],
  ];

  // ---- Wind application --------------------------------------------
  const windApplicationLateral: Vec3 = [0.3, 2.2, 0];
  const windApplicationFrontal: Vec3 = [0.5, 1.5, 0];

  // ---- Camera ------------------------------------------------------
  const cameraCockpit: Vec3 = [-3.3, 1.4, 0];
  const cameraChaseOffset: Vec3 = [-8.3, 3.2, 0];

  return {
    presetId: "antila_30_1",
    centerOfMass,
    centerOfBuoyancy,
    cleats,
    fenderHangers,
    rudderPivot,
    tillerHandle,
    centerboardPivot,
    engineMount,
    propellerPos,
    mastBase,
    mastTop,
    boomAttach,
    buoyancySamples,
    windApplicationLateral,
    windApplicationFrontal,
    cameraCockpit,
    cameraChaseOffset,
  };
}
